import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faCheckCircle,
  faExclamationCircle,
  faTimes,
  faChevronDown,
  faChevronUp,
  faArrowRight,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useAnalysis } from '../context/AnalysisContext';

function GlobalAnalysisWidget() {
  const {
    isAnalyzing,
    progressPercent,
    statusText,
    hasCompleted,
    error,
    jobRole,
    fileCount,
    isMinimized,
    dismissCompletedNotification,
    toggleMinimize,
    resetAnalysis,
  } = useAnalysis();

  const navigate = useNavigate();
  const location = useLocation();

  // Hide widget if nothing active, no error, and not completed
  if (!isAnalyzing && !hasCompleted && !error) {
    return null;
  }

  // If already on /results page and completed, we can hide the notification banner
  if (hasCompleted && location.pathname === '/results') {
    return null;
  }

  const handleViewResults = () => {
    dismissCompletedNotification();
    navigate('/results');
  };

  return (
    <div style={widgetStyles.container}>
      {/* CASE 1: ACTIVE BACKGROUND ANALYSIS */}
      {isAnalyzing && (
        <div style={widgetStyles.cardActive}>
          <div style={widgetStyles.header}>
            <div style={widgetStyles.titleGroup}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#00A4CC', marginRight: '8px' }} />
              <strong style={{ fontSize: '13px', color: '#111' }}>
                AI Parsing {fileCount} Resume{fileCount > 1 ? 's' : ''} ({jobRole})
              </strong>
            </div>
            <button onClick={toggleMinimize} style={widgetStyles.iconButton} title={isMinimized ? 'Expand' : 'Minimize'}>
              <FontAwesomeIcon icon={isMinimized ? faChevronUp : faChevronDown} />
            </button>
          </div>

          {!isMinimized && (
            <div style={widgetStyles.body}>
              <div style={widgetStyles.progressBarBackground}>
                <div
                  style={{
                    ...widgetStyles.progressBarFill,
                    width: `${progressPercent}%`,
                  }}
                />
              </div>
              <p style={widgetStyles.statusText}>{statusText}</p>
              <p style={widgetStyles.subText}>
                ✨ Navigation is unlocked. Browse candidates or tracking safely while AI parses.
              </p>
            </div>
          )}
        </div>
      )}

      {/* CASE 2: COMPLETED NOTIFICATION */}
      {hasCompleted && !isAnalyzing && (
        <div style={widgetStyles.cardCompleted}>
          <div style={widgetStyles.header}>
            <div style={widgetStyles.titleGroup}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#28a745', marginRight: '8px', fontSize: '16px' }} />
              <strong style={{ fontSize: '14px', color: '#155724' }}>
                Resume Analysis Complete!
              </strong>
            </div>
            <button onClick={dismissCompletedNotification} style={widgetStyles.iconButton} title="Dismiss">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div style={widgetStyles.body}>
            <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#2b5435' }}>
              Parsed and match-scored {fileCount} resume{fileCount > 1 ? 's' : ''} for <strong>{jobRole}</strong>.
            </p>
            <button onClick={handleViewResults} style={widgetStyles.viewButton}>
              View Matches <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '6px' }} />
            </button>
          </div>
        </div>
      )}

      {/* CASE 3: ERROR STATE */}
      {error && !isAnalyzing && !hasCompleted && (
        <div style={widgetStyles.cardError}>
          <div style={widgetStyles.header}>
            <div style={widgetStyles.titleGroup}>
              <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#d9534f', marginRight: '8px' }} />
              <strong style={{ fontSize: '13px', color: '#842029' }}>Analysis Failed</strong>
            </div>
            <button onClick={resetAnalysis} style={widgetStyles.iconButton} title="Dismiss">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div style={widgetStyles.body}>
            <p style={{ margin: 0, fontSize: '12px', color: '#842029' }}>{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const widgetStyles = {
  container: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 9999,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  cardActive: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    width: '320px',
    padding: '12px 16px',
    boxShadow: '0 8px 24px rgba(0, 164, 204, 0.25)',
    border: '2px solid #00A4CC',
    transition: 'all 0.3s ease',
  },
  cardCompleted: {
    backgroundColor: '#d4edda',
    borderRadius: '10px',
    width: '320px',
    padding: '12px 16px',
    boxShadow: '0 8px 24px rgba(40, 167, 69, 0.25)',
    border: '1px solid #c3e6cb',
  },
  cardError: {
    backgroundColor: '#f8d7da',
    borderRadius: '10px',
    width: '320px',
    padding: '12px 16px',
    boxShadow: '0 8px 24px rgba(217, 83, 79, 0.25)',
    border: '1px solid #f5c2c7',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  iconButton: {
    background: 'none',
    border: 'none',
    color: '#666',
    cursor: 'pointer',
    padding: '4px',
    fontSize: '12px',
  },
  body: {
    marginTop: '10px',
  },
  progressBarBackground: {
    height: '6px',
    width: '100%',
    backgroundColor: '#e9ecef',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#00A4CC',
    borderRadius: '3px',
    transition: 'width 0.5s ease-in-out',
  },
  statusText: {
    margin: '0 0 4px 0',
    fontSize: '12px',
    color: '#333',
    fontWeight: '500',
  },
  subText: {
    margin: 0,
    fontSize: '11px',
    color: '#666',
    fontStyle: 'italic',
  },
  viewButton: {
    width: '100%',
    padding: '8px 12px',
    backgroundColor: '#28a745',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)',
  },
};

export default GlobalAnalysisWidget;
